/**
 * Timezone Selector Component
 * Allows users to select their preferred timezone
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { COMMON_TIMEZONES, ALL_TIMEZONES } from '@/utils/dateTime';

interface TimezoneSelectorProps {
  value?: string;
  onChange?: (timezone: string) => void;
  showLabel?: boolean;
  showAllTimezones?: boolean;
}

export function TimezoneSelector({
  value,
  onChange,
  showLabel = true,
  showAllTimezones = false,
}: TimezoneSelectorProps) {
  const { t } = useTranslation();
  const [selectedTimezone, setSelectedTimezone] = useState(
    value || 'America/Los_Angeles'
  );

  useEffect(() => {
    if (value) {
      setSelectedTimezone(value);
    }
  }, [value]);

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
    if (onChange) {
      onChange(timezone);
    }
  };

  const base: { value: string; label: string; offset?: string }[] = showAllTimezones ? ALL_TIMEZONES : COMMON_TIMEZONES;
  // TRUS-695: a saved (or browser) zone outside the short list still shows as selected.
  const timezones =
    selectedTimezone && !base.some((tz) => tz.value === selectedTimezone)
      ? [{ value: selectedTimezone, label: selectedTimezone.replace(/_/g, ' ') }, ...base]
      : base;

  return (
    <div className="space-y-2">
      {showLabel && <Label htmlFor="timezone-select">{t('settings.timezone')}</Label>}
      <Select value={selectedTimezone} onValueChange={handleTimezoneChange}>
        <SelectTrigger id="timezone-select" className="w-full">
          <SelectValue placeholder={t('settings.timezone')} />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {timezones.map((tz) => (
            <SelectItem key={tz.value} value={tz.value}>
              <div className="flex items-center justify-between gap-2">
                <span>{tz.label}</span>
                {tz.offset && (
                  <span className="text-xs text-muted-foreground ml-2">{tz.offset}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
